import { useState } from "react";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { isHttpError } from "~/utils/httpClient";
import { usePaymentIntentCreate } from "../../../hooks/api/v2/paymentIntent/usePaymentIntentCreate";
import type {
  PlaceOrderFailureStage,
  ProcessOrderReturnType,
} from "../../stockList/checkoutV2/lib/usePlaceOrder";

type UnifiedCheckoutCardPaymentParams = {
  stripe: Stripe;
  stripeElements?: StripeElements;
  stripePaymentMethodId?: string;
  processOrder: () => Promise<ProcessOrderReturnType>;
  returnUrl: (_orderId: number) => string;
};

type CheckoutErrorResponse = {
  message?: string;
  uuid?: string;
};

const getOrderCreationFailureMessage = (error: unknown) => {
  const response = isHttpError<CheckoutErrorResponse>(error)
    ? error.response
    : undefined;
  const status = response?.status;
  const message = response?.data?.message;
  const traceId = response?.data?.uuid;

  if (status !== undefined && status >= 400 && status < 500 && message) {
    return message;
  }

  return traceId
    ? `We couldn't place your order. Please try again. Reference: ${traceId}`
    : "We couldn't place your order. Please try again.";
};

export const useUnifiedCheckoutCardPayment = ({
  onError,
  onSettled,
}: {
  onError: (
    _stage: PlaceOrderFailureStage,
    _error: unknown,
    _orderId?: number,
  ) => void;
  onSettled: (_orderId: number) => Promise<void>;
}) => {
  const paymentIntentCreate = usePaymentIntentCreate();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");

  const payWithCard = async ({
    stripe,
    stripeElements,
    stripePaymentMethodId,
    processOrder,
    returnUrl,
  }: UnifiedCheckoutCardPaymentParams) => {
    setError("");
    setIsPending(true);
    let createdOrderId: number | undefined;

    try {
      if (!stripePaymentMethodId && stripeElements) {
        const submission = await stripeElements.submit();
        if (submission.error) {
          setError(submission.error.message ?? "Unable to validate your card.");
          onError("payment", submission.error);
          return;
        }
      }

      const { data: order, error: orderError } = await processOrder();
      if (orderError) {
        setError(getOrderCreationFailureMessage(orderError));
        onError("order-creation", orderError);
        return;
      }
      createdOrderId = order.id;

      let paymentIntent: { clientSecret: string };
      try {
        paymentIntent = await paymentIntentCreate.mutateAsync({
          orderId: order.id,
          stripePaymentMethodId,
        });
      } catch (paymentIntentError) {
        setError("We couldn't start your card payment.");
        onError("payment", paymentIntentError, order.id);
        return;
      }

      let confirmation: Awaited<ReturnType<Stripe["confirmPayment"]>>;
      try {
        confirmation = await stripe.confirmPayment({
          elements: stripePaymentMethodId ? undefined : stripeElements,
          clientSecret: paymentIntent.clientSecret,
          confirmParams: { return_url: returnUrl(order.id) },
        });
      } catch (confirmationError) {
        setError("Your payment failed. The order was still placed.");
        onError("payment", confirmationError, order.id);
        return;
      }

      if (confirmation.error) {
        setError(
          confirmation.error.message ??
            "Your payment failed. Please try again or choose a different card.",
        );
        onError("payment", confirmation.error, order.id);
        return;
      }
    } finally {
      try {
        if (createdOrderId != null) {
          await onSettled(createdOrderId);
        }
      } finally {
        setIsPending(false);
      }
    }
  };

  return {
    error,
    isPending,
    payWithCard,
    setError,
  };
};
